import { useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getUtmParamsFromCookies, storeUtmParams } from "@/utils/utmUtils";
import { useTranslation } from "react-i18next";

interface GoogleAuthButtonProps {
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  termsAccepted: boolean;
}

export const GoogleAuthButton = ({
  isLoading,
  setIsLoading,
  termsAccepted
}: GoogleAuthButtonProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // Get UTM parameters from cookies (primary) with URL fallback
  const cookieParams = getUtmParamsFromCookies();
  const utmSource = cookieParams.utm_source || searchParams.get("utm_source") || "";
  const utmMedium = cookieParams.utm_medium || searchParams.get("utm_medium") || "";
  const utmCampaign = cookieParams.utm_campaign || searchParams.get("utm_campaign") || "";
  const utmContent = cookieParams.utm_content || searchParams.get("utm_content") || "";
  const utmTerm = cookieParams.utm_term || searchParams.get("utm_term") || "";
  const referrer = cookieParams.referrer || document.referrer || "";

  const handleGoogleSignIn = async () => {
    if (!termsAccepted) {
      toast({
        variant: "destructive",
        title: t('auth.termsRequired'),
        description: t('auth.mustAcceptTerms'),
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Store UTM parameters in localStorage as backup before redirecting
      storeUtmParams(utmSource, utmMedium, utmCampaign, utmContent, utmTerm, referrer);
      console.log('Stored UTM parameters before Google redirect (from cookies):', {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
        referrer: referrer
      });
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
            // Pass UTM parameters as query parameters
            utm_source: utmSource,
            utm_medium: utmMedium,
            utm_campaign: utmCampaign,
            utm_content: utmContent,
            utm_term: utmTerm,
            referrer: referrer
          }
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Note: We don't need to handle navigation here as the OAuth redirect will happen automatically
    } catch (error: any) {
      console.error("Google sign in error:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message || t('auth.failedToSignInWithGoogle'),
      });
      setIsLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="w-full flex items-center justify-center gap-2 h-10"
      onClick={handleGoogleSignIn}
      disabled={isLoading}
    >
      {isLoading ? (
        <span className="h-4 w-4 border-2 border-current border-t-transparent animate-spin rounded-full"></span>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          viewBox="0 0 48 48"
        >
          <path
            fill="#FFC107"
            d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
          />
          <path
            fill="#FF3D00"
            d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
          />
          <path
            fill="#4CAF50"
            d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
          />
          <path
            fill="#1976D2"
            d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
          />
        </svg>
      )}
      <span>{t('auth.continueWithGoogle')}</span>
    </Button>
  );
};
